import React, { useState } from "react";
import {
  FiCheckCircle,
  FiClock,
  FiMail,
  FiMapPin,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/contactus.scss";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  orderNumber: "",
  subject: "",
  message: "",
  consent: false,
};

function Contact() {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (event) => {
    const { name, type, checked, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((current) => ({ ...current, [name]: "" }));
    setShowSuccess(false);
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.firstName.trim())
      nextErrors.firstName = "Please enter your first name.";
    if (!formData.lastName.trim())
      nextErrors.lastName = "Please enter your last name.";
    if (!formData.email.trim()) {
      nextErrors.email = "Please enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Please enter a valid email address.";
    }
    if (!formData.subject) nextErrors.subject = "Please choose a topic.";
    if (formData.message.trim().length < 10) {
      nextErrors.message =
        "Please tell us a little more (at least 10 characters).";
    }
    if (!formData.consent)
      nextErrors.consent = "Please confirm the privacy notice.";
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstInvalidField = document.getElementById(
        Object.keys(nextErrors)[0]
      );
      firstInvalidField?.focus();
      return;
    }

    setErrors({});
    setShowSuccess(true);
    setFormData(emptyForm);
  };

  return (
    <main className="contact-page">
      <header className="contact-page__hero">
        <p>Customer care</p>
        <h1>How can we help?</h1>
        <span>
          Questions about an order, a product or your return? Send us a note and
          our team will get back to you.
        </span>
      </header>

      <div className="contact-page__layout">
        <section
          className="contact-page__form-section"
          aria-labelledby="contact-form-title"
        >
          <div className="contact-page__section-head">
            <p>Send a request</p>
            <h2 id="contact-form-title">Tell us what you need.</h2>
          </div>

          {showSuccess && (
            <div className="contact-page__success" role="status">
              <FiCheckCircle aria-hidden="true" />
              <div>
                <strong>Your message is on its way.</strong>
                <p>
                  Thank you for contacting {siteConfig.displayName}. We will
                  reply as soon as possible.
                </p>
              </div>
            </div>
          )}

          <form
            className="contact-page__form"
            onSubmit={handleSubmit}
            noValidate
          >
            <div className="contact-page__field-row">
              <div className="contact-page__field">
                <label htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={
                    errors.firstName ? "firstName-error" : undefined
                  }
                />
                {errors.firstName && (
                  <small id="firstName-error">{errors.firstName}</small>
                )}
              </div>
              <div className="contact-page__field">
                <label htmlFor="lastName">Last name *</label>
                <input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={
                    errors.lastName ? "lastName-error" : undefined
                  }
                />
                {errors.lastName && (
                  <small id="lastName-error">{errors.lastName}</small>
                )}
              </div>
            </div>

            <div className="contact-page__field-row">
              <div className="contact-page__field">
                <label htmlFor="email">Email address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && <small id="email-error">{errors.email}</small>}
              </div>
              <div className="contact-page__field">
                <label htmlFor="phone">Phone number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  autoComplete="tel"
                />
              </div>
            </div>

            <div className="contact-page__field-row">
              <div className="contact-page__field">
                <label htmlFor="subject">Topic *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.subject)}
                  aria-describedby={
                    errors.subject ? "subject-error" : undefined
                  }
                >
                  <option value="">Choose a topic</option>
                  <option value="order">Order status</option>
                  <option value="product">Product advice</option>
                  <option value="return">Return or refund</option>
                  <option value="delivery">Delivery</option>
                  <option value="other">Something else</option>
                </select>
                {errors.subject && (
                  <small id="subject-error">{errors.subject}</small>
                )}
              </div>
              <div className="contact-page__field">
                <label htmlFor="orderNumber">Order number</label>
                <input
                  id="orderNumber"
                  name="orderNumber"
                  value={formData.orderNumber}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="contact-page__field contact-page__field--wide">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows="7"
                value={formData.message}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? "message-error" : undefined}
                placeholder="How can we help?"
              />
              {errors.message && (
                <small id="message-error">{errors.message}</small>
              )}
            </div>

            <div className="contact-page__consent">
              <input
                id="consent"
                name="consent"
                type="checkbox"
                checked={formData.consent}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.consent)}
                aria-describedby={errors.consent ? "consent-error" : undefined}
              />
              <label htmlFor="consent">
                I have read the <Link to="/privacy">privacy policy</Link> and
                agree that my details may be used to answer this request.
              </label>
              {errors.consent && (
                <small id="consent-error">{errors.consent}</small>
              )}
            </div>

            <button className="contact-page__submit" type="submit">
              Send message
            </button>
          </form>
        </section>

        <aside
          className="contact-page__details"
          aria-labelledby="contact-details-title"
        >
          <p>Talk to us</p>
          <h2 id="contact-details-title">Customer service</h2>
          <ul>
            <li>
              <FiMail aria-hidden="true" />
              <div>
                <span>Email</span>
                <a href={`mailto:${siteConfig.contact.email}`}>
                  {siteConfig.contact.email}
                </a>
              </div>
            </li>

            <li>
              <FiClock aria-hidden="true" />
              <div>
                <span>Opening hours</span>
                <strong>{siteConfig.contact.hours}</strong>
              </div>
            </li>
            <li>
              <FiMapPin aria-hidden="true" />
              <div>
                <span>Company &amp; address</span>
                <strong>{siteConfig.legalName}</strong>
                <strong>{siteConfig.contact.location}</strong>
              </div>
            </li>
          </ul>
          <div className="contact-page__quick-links">
            <h3>Looking for a quick answer?</h3>
            <Link to="/shipping">
              Shipping & delivery <span aria-hidden="true">→</span>
            </Link>
            <Link to="/returns">
              Returns & refunds <span aria-hidden="true">→</span>
            </Link>
            <Link to="/terms">
              Terms & conditions <span aria-hidden="true">→</span>
            </Link>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default Contact;
